import angular, { IPromise } from 'angular'
import { ApiTax } from '@vend/types'
import { LineItem } from '../models/line-item'
import { Sale } from '../models/sale'
import {
  LineItemPricing,
  PromotionOptions,
  calculateGiftCardLineItemPricing,
  calculateLineItemPricing,
} from '../sale-calculation/calculate-line-item'
import { calculateTotalTaxRate } from '../sale-calculation/calculate-tax'
import { isGiftCard } from '../functions/lineItem'
import {
  registerManager,
  priceBookManager,
  taxManager,
  retailerSettingsManager,
} from '../ng-compat/managers'
import { FEATURE_REGISTER_BASEPRICE_AS_BESTPRICE } from '../constants/wr-features'
import { getFeatureFromCache } from '../util/feature-cache'

export interface PriceLineItemOptions extends PromotionOptions {
  forceRefresh?: boolean
  isUpdatingCustomer?: boolean
  /**
   * When set, overrides the `register_baseprice_as_bestprice` feature flag for this calculation.
   */
  usePricebookBestPriceAsBase?: boolean
}

interface LineItemTaxInfo {
  taxInfo: ApiTax | undefined
  customerTaxApplied?: boolean
}

/**
 * Manages unit price, tax and loyalty calculation.
 *
 * @class UnitPriceManager
 */
export class UnitPriceManager {
  private _whenInitialised: IPromise<void> | undefined

  /**
   * Initialise the manager.
   *
   * @method init
   *
   * @return {Promise} A promise resolved once initialisation is complete
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = priceBookManager.init()
    }
    return this._whenInitialised
  }

  /**
   * Get the tax info of the given line item including whether or not a customer tax was applied. By default will only
   * fetch the latest tax info if there isn't already tax info on the line item unless the `forceRefresh` option is
   * specified.
   *
   * @method getTaxInfoForLineItem
   *
   * @param  lineItem {LineItem}
   *         The line item to update the tax info for
   *
   * @param  [options] {Object}
   *
   *         @param  [options.forceRefresh] {Boolean}
   *                 If set to true will force the refresh of the tax info even if it is already set
   *
   * @return {Promise<{ taxInfo, customerTaxApplied }>} The tax info and customer tax applied status for the given
   *        LineItem or a promise therefor. This is unfortunately necessary to accommodate the combination of $q and
   *        normal promises when testing old and new implementations of priceLineItem in the same test suite.
   */
  public getTaxInfoForLineItem(
    lineItem: Readonly<LineItem>,
    customerId?: string,
    options?: {
      forceRefresh?: boolean
      isUpdatingCustomer?: boolean
    }
  ): LineItemTaxInfo | IPromise<LineItemTaxInfo> {
    let forceRefreshTaxRate = options?.forceRefresh
    // If the line item has been adjusted, the tax might have too, so we won't try to refresh it
    // when it's not adding/removing customer and there is no customer tax applied
    if (
      lineItem.adjusted &&
      !options?.isUpdatingCustomer &&
      !lineItem.customerTaxApplied
    ) {
      forceRefreshTaxRate = false
    }

    if (lineItem.taxInfo && !forceRefreshTaxRate) {
      return {
        taxInfo: lineItem.taxInfo,
        customerTaxApplied: lineItem.customerTaxApplied,
      }
    }

    return taxManager
      .getTaxForProduct(lineItem.product.id, customerId)
      .then(({ tax, taxType }) => ({
        taxInfo: tax,
        customerTaxApplied: taxType === 'customer',
      }))
  }

  /**
   * Get the pricing details for the given line item in context of the given sale. This function is pure and should
   * have no side effects or mutation of state.
   *
   * @param {Sale} sale
   *        The sale the given line item belongs to
   *
   * @param {LineItem} lineItem
   *        The line item to get the price information for
   *
   * @return {Promise<LineItemPricing>} A promise resolved with the calculated pricing for the given line item
   */
  public async getLineItemPricing(
    sale: Readonly<Sale>,
    lineItem: Readonly<LineItem>,
    taxInfo: Readonly<ApiTax> | undefined,
    options: Readonly<PriceLineItemOptions> = {}
  ): Promise<LineItemPricing> {
    // External state
    const isTaxInclusive = registerManager.isTaxInclusive()
    const loyaltyEnabled = retailerSettingsManager.isLoyaltyEnabled()
    const loyaltyRatio = retailerSettingsManager.getLoyaltyRatio()

    // Async dependencies
    const priceBookInfos = isGiftCard(lineItem.product)
      ? []
      : await priceBookManager.getPriceBookInfoForLineItem(
          sale.lineItems,
          sale.customer,
          lineItem
        )

    // Pure calculations
    const taxRate = calculateTotalTaxRate(taxInfo?.rates || [])

    if (isGiftCard(lineItem.product)) {
      return calculateGiftCardLineItemPricing(lineItem.basePrice, taxRate)
    }

    const usePricebookBestPriceAsBase =
      options.usePricebookBestPriceAsBase ??
      getFeatureFromCache(FEATURE_REGISTER_BASEPRICE_AS_BESTPRICE)

    return calculateLineItemPricing(
      lineItem,
      priceBookInfos,
      taxRate,
      options,
      isTaxInclusive,
      loyaltyEnabled,
      loyaltyRatio,
      usePricebookBestPriceAsBase
    )
  }
}

export const unitPriceManager = new UnitPriceManager()
/**
 * Manager for unit price related operations.
 *
 * @module unitPriceManager
 */
angular
  .module('webregister.managers')
  .factory('unitPriceManager', () => unitPriceManager)
